import React, { useState, useEffect, useRef } from 'react';
import Layout, { PageKey } from '../components/Layout';
import {
  startAdBatch, adAgentStreamUrl, listAdBatches, getAdBatch,
  approveAd, rejectAd, approveAdBatch, adImageUrl, exportAdBatchUrl,
  searchTrendtrack, getSwipes, addSwipe, deleteSwipe, getAdAgentHealth,
} from '../api';

interface Props {
  user: { email: string; name: string };
  onLogout: () => void;
  onNavigate: (page: PageKey) => void;
}

type Tab = 'agent' | 'batches' | 'swipe';

export default function AdAgent({ user, onLogout, onNavigate }: Props) {
  const [tab, setTab] = useState<Tab>('agent');
  const [health, setHealth] = useState<any>(null);

  useEffect(() => { getAdAgentHealth().then(r => setHealth(r.data)).catch(() => {}); }, []);

  return (
    <Layout user={user} onLogout={onLogout} activePage="adagent" onNavigate={onNavigate}>
      <div style={s.page}>
        <div style={s.header}>
          <div>
            <h1 style={s.h1}>Ad-Test-Agent</h1>
            <p style={s.sub}>Bild-Ads generieren, reviewen und als Entwurf sammeln.</p>
          </div>
          {health && <KeyBadges keys={health.keys} />}
        </div>

        <div style={s.tabs}>
          <TabBtn active={tab === 'agent'} onClick={() => setTab('agent')}>Agent</TabBtn>
          <TabBtn active={tab === 'batches'} onClick={() => setTab('batches')}>Batches</TabBtn>
          <TabBtn active={tab === 'swipe'} onClick={() => setTab('swipe')}>Swipe (TrendTrack)</TabBtn>
        </div>

        {tab === 'agent' && <AgentTab onDone={() => setTab('batches')} />}
        {tab === 'batches' && <BatchesTab />}
        {tab === 'swipe' && <SwipeTab />}
      </div>
    </Layout>
  );
}

// ── Agent-Tab ──────────────────────────────────────────────────────────────────

function AgentTab({ onDone }: { onDone: () => void }) {
  const [offer, setOffer] = useState('HEKA (Herbstkampagne)');
  const [count, setCount] = useState(5);
  const [brief, setBrief] = useState('');
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<{ type: string; msg: string }[]>([]);
  const esRef = useRef<EventSource | null>(null);
  const logEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => { esRef.current?.close(); }, []);
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [log]);

  const start = async () => {
    setRunning(true); setLog([]);
    try {
      const { data } = await startAdBatch({ offer, count, brief });
      const es = new EventSource(adAgentStreamUrl(data.job_id));
      esRef.current = es;
      es.onmessage = (e) => {
        try { setLog(l => [...l, JSON.parse(e.data)]); } catch { /* ignore */ }
      };
      es.addEventListener('end', () => { es.close(); setRunning(false); });
      es.onerror = () => { es.close(); setRunning(false); };
    } catch (err: any) {
      setLog(l => [...l, { type: 'error', msg: err?.response?.data?.error || 'Start fehlgeschlagen' }]);
      setRunning(false);
    }
  };

  const iconFor = (t: string) => t === 'ad' ? '✅' : t === 'tool' ? '⚙️' : t === 'error' ? '⛔' : t === 'done' ? '🏁' : '·';

  return (
    <div style={s.grid2}>
      <div style={s.card}>
        <label style={s.label}>Angebot / Kampagne</label>
        <input style={s.input} value={offer} onChange={e => setOffer(e.target.value)} disabled={running} />

        <label style={s.label}>Anzahl Ads</label>
        <input style={s.input} type="number" min={1} max={20} value={count}
          onChange={e => setCount(Math.max(1, Math.min(20, +e.target.value || 1)))} disabled={running} />

        <label style={s.label}>Brief / Kontext (optional)</label>
        <textarea style={{ ...s.input, minHeight: 90, resize: 'vertical' }} value={brief}
          onChange={e => setBrief(e.target.value)} disabled={running}
          placeholder="z. B. Studio, Zielgruppe, Ton, No-Gos …" />

        <button style={{ ...s.btnPrimary, ...(running ? s.btnDisabled : {}) }} onClick={start} disabled={running}>
          {running ? 'Läuft …' : `${count} Ads generieren`}
        </button>
        <p style={s.hint}>Der Agent recherchiert TrendTrack, schreibt Copy und generiert Bilder via Higgsfield.
          Alles landet als Entwurf – kein Meta-Push.</p>
      </div>

      <div style={s.card}>
        <label style={s.label}>Live-Fortschritt</label>
        <div style={s.logBox}>
          {log.length === 0 && <div style={s.logEmpty}>Noch nichts gestartet.</div>}
          {log.map((l, i) => (
            <div key={i} style={{ ...s.logLine, ...(l.type === 'error' ? { color: '#c0392b' } : l.type === 'ad' ? { color: '#1a7a3a' } : {}) }}>
              <span style={{ opacity: 0.6, marginRight: 6 }}>{iconFor(l.type)}</span>{l.msg}
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
        {!running && log.some(l => l.type === 'done') &&
          <button style={s.btnSecondary} onClick={onDone}>→ Zu den Batches</button>}
      </div>
    </div>
  );
}

// ── Batches-Tab ────────────────────────────────────────────────────────────────

function BatchesTab() {
  const [batches, setBatches] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);

  const load = () => listAdBatches().then(r => setBatches(r.data.batches)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (id: string) => getAdBatch(id).then(r => setSelected(r.data)).catch(() => {});

  const act = async (fn: Promise<any>) => { await fn; if (selected) open(selected.id); load(); };

  if (selected) {
    const ads = selected.ads || [];
    return (
      <div>
        <div style={s.detailHead}>
          <button style={s.linkBtn} onClick={() => { setSelected(null); load(); }}>← Batches</button>
          <div style={{ flex: 1 }}>
            <div style={s.detailTitle}>{selected.name}</div>
            <div style={s.detailMeta}>{selected.offer} · {ads.length} Ads · Status: {selected.status}</div>
          </div>
          <a style={s.btnSecondary} href={exportAdBatchUrl(selected.id)}>Export (ZIP)</a>
          <button style={s.btnPrimary} onClick={() => act(approveAdBatch(selected.id))}>Alle offenen approven</button>
        </div>
        {ads.length === 0 && <div style={s.empty}>Noch keine Ads in diesem Batch (evtl. läuft die Generierung noch).</div>}
        <div style={s.adGrid}>
          {ads.map((ad: any) => (
            <div key={ad.id} style={{ ...s.adCard, ...(ad.status === 'approved' ? s.adApproved : ad.status === 'rejected' ? s.adRejected : {}) }}>
              <div style={s.adImgWrap}>
                {ad.image_path
                  ? <img src={adImageUrl(ad.id)} alt={ad.headline} style={s.adImg} />
                  : <div style={s.adNoImg}>kein Bild</div>}
                <span style={s.adStatus}>{ad.status}</span>
              </div>
              <div style={s.adBody}>
                <div style={s.adAngle}>{ad.angle}</div>
                <div style={s.adHeadline}>{ad.headline}</div>
                <div style={s.adText}>{ad.primary_text}</div>
              </div>
              <div style={s.adActions}>
                <button style={s.btnApprove} onClick={() => act(approveAd(ad.id))}>✓ Approve</button>
                <button style={s.btnReject} onClick={() => act(rejectAd(ad.id))}>✕ Reject</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {batches.length === 0 && <div style={s.empty}>Noch keine Batches. Starte einen im Tab „Agent".</div>}
      <div style={s.batchList}>
        {batches.map(b => (
          <div key={b.id} style={s.batchRow} onClick={() => open(b.id)}>
            <div style={{ flex: 1 }}>
              <div style={s.batchName}>{b.name}</div>
              <div style={s.batchMeta}>{b.offer} · {b.ad_count} Ads · {b.created_at}</div>
            </div>
            <span style={{ ...s.pill, ...(b.status === 'approved' ? s.pillGreen : b.status === 'generating' ? s.pillAmber : {}) }}>{b.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Swipe-Tab ──────────────────────────────────────────────────────────────────

function SwipeTab() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [swipes, setSwipes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const loadSwipes = () => getSwipes().then(r => setSwipes(r.data.swipes)).catch(() => {});
  useEffect(() => { loadSwipes(); }, []);

  const search = async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await searchTrendtrack(q);
      setResults(data.ads || []);
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Suche fehlgeschlagen');
      setResults([]);
    }
    setLoading(false);
  };

  const keep = async (ad: any) => {
    await addSwipe({
      trendtrack_ad_id: ad.ad_id, thumbnail_url: ad.thumbnail_url,
      headline: ad.headline, primary_text: ad.primary_text, landing_url: ad.landing_url,
    });
    loadSwipes();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input style={{ ...s.input, marginBottom: 0, flex: 1 }} value={q}
          onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="TrendTrack durchsuchen, z. B. Fitnessstudio Rücken" />
        <button style={s.btnPrimary} onClick={search} disabled={loading}>{loading ? '…' : 'Suchen'}</button>
      </div>
      {err && <div style={s.errBox}>{err}</div>}

      <div style={s.adGrid}>
        {results.map(ad => (
          <div key={ad.ad_id} style={s.adCard}>
            <div style={s.adImgWrap}>
              {ad.thumbnail_url ? <img src={ad.thumbnail_url} alt="" style={s.adImg} /> : <div style={s.adNoImg}>kein Bild</div>}
            </div>
            <div style={s.adBody}>
              <div style={s.adAngle}>{ad.brand}</div>
              <div style={s.adHeadline}>{ad.headline}</div>
              <div style={s.adText}>{ad.primary_text}</div>
            </div>
            <div style={s.adActions}>
              <button style={s.btnApprove} onClick={() => keep(ad)}>+ Merken</button>
              {ad.landing_url && <a style={s.btnReject} href={ad.landing_url} target="_blank" rel="noreferrer">LP ↗</a>}
            </div>
          </div>
        ))}
      </div>

      <h3 style={{ ...s.label, marginTop: 24 }}>Swipe-File ({swipes.length})</h3>
      <div style={s.swipeList}>
        {swipes.map(sw => (
          <div key={sw.id} style={s.swipeRow}>
            {sw.thumbnail_url && <img src={sw.thumbnail_url} alt="" style={s.swipeThumb} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={s.batchName}>{sw.headline || '(ohne Titel)'}</div>
              <div style={s.batchMeta}>{(sw.primary_text || '').slice(0, 120)}</div>
            </div>
            <button style={s.linkBtn} onClick={() => { deleteSwipe(sw.id).then(loadSwipes); }}>entfernen</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── kleine Helfer ────────────────────────────────────────────────────────────────

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button style={{ ...s.tabBtn, ...(active ? s.tabActive : {}) }} onClick={onClick}>{children}</button>;
}

function KeyBadges({ keys }: { keys: Record<string, boolean> }) {
  const label: Record<string, string> = { higgsfield: 'Higgsfield', trendtrack: 'TrendTrack', anthropic: 'Claude' };
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {Object.entries(keys).map(([k, ok]) => (
        <span key={k} style={{ ...s.keyBadge, ...(ok ? s.keyOk : s.keyMissing) }}>
          {ok ? '●' : '○'} {label[k] || k}
        </span>
      ))}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { padding: '32px 40px', maxWidth: 1180, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  h1: { fontSize: 24, fontWeight: 800, color: '#1d1d1f', margin: 0, letterSpacing: '-0.4px' },
  sub: { fontSize: 13.5, color: '#6e6e73', margin: '4px 0 0' },
  tabs: { display: 'flex', gap: 6, marginBottom: 22, borderBottom: '1px solid #e8e8ed' },
  tabBtn: { background: 'transparent', border: 'none', padding: '10px 14px', fontSize: 13.5, fontWeight: 600, color: '#6e6e73', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1 },
  tabActive: { color: '#1a3a5c', borderBottom: '2px solid #1a3a5c' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  card: { background: '#fff', border: '1px solid #e8e8ed', borderRadius: 14, padding: 20 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 0 6px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d2d2d7', borderRadius: 9, fontSize: 14, marginBottom: 14, outline: 'none', fontFamily: 'inherit' },
  btnPrimary: { background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
  btnSecondary: { background: '#eef1f5', color: '#1a3a5c', border: 'none', borderRadius: 9, padding: '10px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', marginTop: 12, textDecoration: 'none', display: 'inline-block' },
  btnDisabled: { opacity: 0.5, cursor: 'default' },
  hint: { fontSize: 12, color: '#aeaeb2', marginTop: 12, lineHeight: 1.5 },
  logBox: { background: '#0f1115', color: '#d0d4dc', borderRadius: 9, padding: 14, height: 300, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.7 },
  logEmpty: { color: '#6b7280' },
  logLine: { whiteSpace: 'pre-wrap' as const },
  detailHead: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 },
  detailTitle: { fontSize: 17, fontWeight: 700, color: '#1d1d1f' },
  detailMeta: { fontSize: 12.5, color: '#6e6e73', marginTop: 2 },
  linkBtn: { background: 'transparent', border: 'none', color: '#1a3a5c', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 },
  empty: { color: '#aeaeb2', fontSize: 14, padding: '30px 0' },
  batchList: { display: 'flex', flexDirection: 'column', gap: 8 },
  batchRow: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e8e8ed', borderRadius: 11, padding: '14px 16px', cursor: 'pointer' },
  batchName: { fontSize: 14, fontWeight: 600, color: '#1d1d1f', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  batchMeta: { fontSize: 12, color: '#aeaeb2', marginTop: 2, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  pill: { fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: '#eef1f5', color: '#6e6e73' },
  pillGreen: { background: 'rgba(26,122,58,0.12)', color: '#1a7a3a' },
  pillAmber: { background: 'rgba(200,140,20,0.14)', color: '#a5730f' },
  adGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 },
  adCard: { background: '#fff', border: '1px solid #e8e8ed', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  adApproved: { borderColor: '#1a7a3a', boxShadow: '0 0 0 1px #1a7a3a' },
  adRejected: { opacity: 0.5 },
  adImgWrap: { position: 'relative', width: '100%', aspectRatio: '1 / 1', background: '#f0f0f3' },
  adImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  adNoImg: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aeaeb2', fontSize: 12 },
  adStatus: { position: 'absolute', top: 8, right: 8, fontSize: 10.5, fontWeight: 700, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '3px 8px', borderRadius: 20 },
  adBody: { padding: '12px 14px', flex: 1 },
  adAngle: { fontSize: 11, fontWeight: 700, color: '#1a3a5c', textTransform: 'uppercase', letterSpacing: '0.04em' },
  adHeadline: { fontSize: 14, fontWeight: 700, color: '#1d1d1f', margin: '4px 0 6px' },
  adText: { fontSize: 12.5, color: '#48484a', lineHeight: 1.5, whiteSpace: 'pre-wrap' as const },
  adActions: { display: 'flex', gap: 8, padding: '0 14px 14px' },
  btnApprove: { flex: 1, background: 'rgba(26,122,58,0.12)', color: '#1a7a3a', border: 'none', borderRadius: 8, padding: '8px', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'center', textDecoration: 'none' },
  btnReject: { flex: 1, background: '#f3f3f5', color: '#6e6e73', border: 'none', borderRadius: 8, padding: '8px', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'center', textDecoration: 'none' },
  errBox: { background: 'rgba(192,57,43,0.08)', color: '#c0392b', borderRadius: 9, padding: '10px 14px', fontSize: 13, marginBottom: 14 },
  swipeList: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 },
  swipeRow: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e8e8ed', borderRadius: 11, padding: 10 },
  swipeThumb: { width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 },
  keyBadge: { fontSize: 11.5, fontWeight: 600, padding: '4px 9px', borderRadius: 20 },
  keyOk: { background: 'rgba(26,122,58,0.12)', color: '#1a7a3a' },
  keyMissing: { background: 'rgba(192,57,43,0.1)', color: '#c0392b' },
};
